import React, { useMemo, useCallback, useContext } from 'react';
import { useField } from 'react-final-form';
import { SelectField, Tooltip } from '@ubeya/shared-web/components';
import useAddNewSelector from '@ubeya/shared-web/hooks/useAddNewSelector';
import useCRM from '@ubeya/shared/hooks/account/useCRM';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { ReactComponent as BaseStar } from '@ubeya/shared-web/assets/star-paying-account.svg';
import styled from 'styled-components';
import { ACCOUNT_BILLING_STATUS_MAP } from '@ubeya/shared/constants';
import ClientCard from 'crm/components/ClientCard/ClientCard';
import { TabTypes } from 'crm/constants';

const StarIcon = styled(BaseStar)`
  height: 10px;
  width: 10px;
  margin-left: 8px;
`;

const NameWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const ClientSelectField = ({
  onAdd,
  required,
  onChangeClientAdmin,
  validate,
  onChangeClientBranchId,
  alwaysShowClientAdmin = false,
  disableAddNewClient = false,
  isDisabled = false,
  hideTitle = false,
  disableClientAdminEdit = false,
  className,
  dataTestId
}) => {
  const { t } = useContext(ConfigContext);

  const { activeClients: clients, clientsOptions: defaultClientsOptions, mappedClientBranches } = useCRM();

  const {
    input: { value }
  } = useField('clientBranchId');

  const {
    isOpen: clientModal,
    setIsOpen: setClientModal,
    options: clientsOptions,
    onChange: onChangeClient
  } = useAddNewSelector({
    options: defaultClientsOptions,
    addText: 'addNewClient',
    enable: !disableAddNewClient
  });

  // triggered upon creating new client upon typing, params gets the name typed
  const onChange = useCallback(
    (params) => {
      onChangeClient?.(params);

      /* params is string upon adding new client modal (with pre populated string), 
        and null is upon adding new without pre-name */
      if (typeof params !== 'string' && params !== null) {
        onChangeClientBranchId?.(params);
      }
    },
    [onChangeClient, onChangeClientBranchId]
  );

  const renderClientContactOptions = useMemo(
    () => clients.find(({ branches }) => branches.some(({ id }) => id === value))?.admins,
    [clients, value]
  );

  const {
    isOpen: clientPOCModal,
    setIsOpen: setClientPOCModal,
    options: clientsPOCOptions,
    onChange: onChangeAdminClient
  } = useAddNewSelector({
    options: renderClientContactOptions
      ? renderClientContactOptions.map(({ id, firstName, lastName }) => ({
          value: id,
          label: `${firstName} ${lastName}`
        }))
      : [],

    addText: 'addNewContact',
    enable: !disableAddNewClient
  });

  const formatOption = ({ value: optionId, label, status }) => ({
    value: optionId,
    label: (
      <NameWrapper>
        {label}
        {[ACCOUNT_BILLING_STATUS_MAP.INTERNAL, ACCOUNT_BILLING_STATUS_MAP.PAYING].includes(status) && (
          <Tooltip body={t('supplierEasyAccess')}>
            <StarIcon />
          </Tooltip>
        )}
      </NameWrapper>
    )
  });

  return (
    <>
      <SelectField
        name="clientBranchId"
        title={t('client')}
        options={clientsOptions}
        onChange={onChange}
        isMulti={false}
        validate={validate}
        required={required}
        usePortal
        formatOption={formatOption}
        creatable={!disableAddNewClient}
        clearable
        isDisabled={isDisabled}
        className={className}
        hideTitle={hideTitle}
        dataTestId={dataTestId}
      />

      {(!!value || alwaysShowClientAdmin) && (
        <SelectField
          className="client-admin-select"
          name="clientAdminId"
          title={t('clientAdminContact')}
          options={clientsPOCOptions}
          onChange={onChangeAdminClient}
          isMulti={false}
          usePortal
          clearable
          formatOption={formatOption}
          creatable={!disableAddNewClient}
          isDisabled={disableClientAdminEdit || (alwaysShowClientAdmin && !value)}
        />
      )}

      {clientModal && !clientPOCModal && (
        <ClientCard
          defaultData={typeof clientModal === 'string' ? { name: clientModal } : {}}
          onClose={() => setClientModal(false)}
          onConfirm={({ data }) => {
            onAdd(data?.branches?.[0]?.id);
            setClientModal(false);
          }}
        />
      )}

      {clientPOCModal && (
        <ClientCard
          defaultData={{
            contactName: typeof clientPOCModal === 'string' ? clientPOCModal : '',
            id: mappedClientBranches[value]?.id
          }}
          defaultActiveTab={TabTypes.CONTACTS}
          onClose={() => setClientPOCModal(false)}
          onConfirm={({ data }) => {
            const admins = data?.branches?.[0]?.admins;
            onChangeClientAdmin(admins[admins.length - 1]?.id);
            setClientPOCModal(false);
          }}
        />
      )}
    </>
  );
};

export default ClientSelectField;
